import { EditorSelection, type ChangeSpec } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';

/** Markdown formatting actions the toolbar / shortcuts can apply. */
export type FormatAction =
  | 'bold'
  | 'italic'
  | 'strike'
  | 'code'
  | 'codeblock'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'quote'
  | 'ul'
  | 'ol'
  | 'task'
  | 'link';

/** Wrap each selection in `before`/`after`, or unwrap if already wrapped. */
function wrap(view: EditorView, before: string, after = before): void {
  view.dispatch(
    view.state.changeByRange((range) => {
      const text = view.state.sliceDoc(range.from, range.to);
      const already =
        text.startsWith(before) &&
        text.endsWith(after) &&
        text.length >= before.length + after.length;
      if (already) {
        const inner = text.slice(before.length, text.length - after.length);
        return {
          changes: { from: range.from, to: range.to, insert: inner },
          range: EditorSelection.range(range.from, range.from + inner.length),
        };
      }
      const wrapped = `${before}${text}${after}`;
      return {
        changes: { from: range.from, to: range.to, insert: wrapped },
        range: EditorSelection.range(
          range.from + before.length,
          range.from + before.length + text.length,
        ),
      };
    }),
  );
  view.focus();
}

/** Toggle a line prefix (heading/quote/list) on every line the selection spans. */
function toggleLinePrefix(view: EditorView, prefix: string): void {
  const { state } = view;
  const changes: ChangeSpec[] = [];
  const seen = new Set<number>();
  for (const range of state.selection.ranges) {
    const first = state.doc.lineAt(range.from).number;
    const last = state.doc.lineAt(range.to).number;
    for (let n = first; n <= last; n++) {
      if (seen.has(n)) continue;
      seen.add(n);
      const line = state.doc.line(n);
      if (line.text.startsWith(prefix)) {
        changes.push({ from: line.from, to: line.from + prefix.length, insert: '' });
      } else {
        changes.push({ from: line.from, insert: prefix });
      }
    }
  }
  view.dispatch({ changes });
  view.focus();
}

function insertFenced(view: EditorView): void {
  const range = view.state.selection.main;
  const text = view.state.sliceDoc(range.from, range.to);
  const atLineStart = view.state.doc.lineAt(range.from).from === range.from;
  const lead = atLineStart ? '' : '\n';
  const block = `${lead}\`\`\`\n${text}\n\`\`\`\n`;
  const caret = range.from + lead.length + 4; // just after the opening fence
  view.dispatch({
    changes: { from: range.from, to: range.to, insert: block },
    selection: { anchor: caret },
  });
  view.focus();
}

function insertLink(view: EditorView): void {
  view.dispatch(
    view.state.changeByRange((range) => {
      const text = view.state.sliceDoc(range.from, range.to) || 'text';
      const insert = `[${text}](url)`;
      const urlStart = range.from + 1 + text.length + 2;
      return {
        changes: { from: range.from, to: range.to, insert },
        range: EditorSelection.range(urlStart, urlStart + 3),
      };
    }),
  );
  view.focus();
}

/** Apply a formatting action to the editor view. */
export function applyFormat(view: EditorView, action: FormatAction): void {
  switch (action) {
    case 'bold':
      return wrap(view, '**');
    case 'italic':
      return wrap(view, '_');
    case 'strike':
      return wrap(view, '~~');
    case 'code':
      return wrap(view, '`');
    case 'codeblock':
      return insertFenced(view);
    case 'h1':
      return toggleLinePrefix(view, '# ');
    case 'h2':
      return toggleLinePrefix(view, '## ');
    case 'h3':
      return toggleLinePrefix(view, '### ');
    case 'quote':
      return toggleLinePrefix(view, '> ');
    case 'ul':
      return toggleLinePrefix(view, '- ');
    case 'ol':
      return toggleLinePrefix(view, '1. ');
    case 'task':
      return toggleLinePrefix(view, '- [ ] ');
    case 'link':
      return insertLink(view);
  }
}
